import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Building2,
  ExternalLink,
  FileText,
  Loader2,
  Lock,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { leadsAPI, openapiAPI } from '../../utils/api';
import type { LeadResponse, LeadUpdate } from '../../types/lead';
import type {
  OpenapiSearchResultItem,
  OpenapiVisuraStatusResponse,
} from '../../types/openapi';
import {
  OPENAPI_QUOTA_QUERY_KEY,
  useOpenapiQuota,
} from '../../hooks/useOpenapiQuota';
import RequestOpenapiUpgradeModal, {
  UpgradeMode,
} from './RequestOpenapiUpgradeModal';
import RequestOpenapiActivationModal from './RequestOpenapiActivationModal';

interface Props {
  lead: LeadResponse;
}

// Polling: 5s × 12 = 60s max prima del messaggio "ci vuole più tempo"
const VISURA_POLL_INTERVAL_MS = 5_000;
const VISURA_POLL_MAX_ATTEMPTS = 12;

const MOCK_DATA = {
  denominazione: 'ESEMPIO S.R.L.',
  indirizzo: 'Via Roma 1, 20100 Milano (MI)',
  capitale: '€100.000',
  ateco: '62.01.00 - Produzione di software',
  pec: 'esempio@pec.example.it',
};

export default function OpenapiDataPanel({ lead }: Props) {
  const queryClient = useQueryClient();
  const quotaQuery = useOpenapiQuota(true);
  const featureEnabled = quotaQuery.data?.feature_enabled ?? false;
  const advancedRemaining = quotaQuery.data
    ? Math.max(quotaQuery.data.advanced.limit - quotaQuery.data.advanced.used, 0)
    : null;
  const visuraRemaining = quotaQuery.data
    ? Math.max(quotaQuery.data.visura.limit - quotaQuery.data.visura.used, 0)
    : null;

  const [activationOpen, setActivationOpen] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState<UpgradeMode | null>(null);
  const [visuraExternalId, setVisuraExternalId] = useState<string | null>(null);
  const [visuraTimedOut, setVisuraTimedOut] = useState(false);
  const visuraAttempts = useRef(0);

  const handleOpenapiError = (
    error: any,
    lookupType: 'advanced' | 'visura' | 'base'
  ) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 403 && detail === 'feature_disabled') {
      toast.error('Integrazione anagrafica imprese non attiva sul piano corrente');
      setActivationOpen(true);
      return;
    }
    if (status === 429 && detail === 'quota_exceeded') {
      toast.error('Quota esaurita');
      setUpgradeMode(lookupType === 'visura' ? 'quota_visura' : 'quota_advanced');
      return;
    }
    if (status === 503) {
      toast.error('Verifica quota non disponibile, riprova');
      return;
    }
    if (status === 502) {
      toast.error('Servizio temporaneamente non disponibile, riprova');
      return;
    }
    toast.error(typeof detail === 'string' ? detail : 'Errore durante la chiamata OpenAPI');
  };

  // ----- Refresh anagrafica (search by P.IVA) -----
  const refreshMutation = useMutation({
    mutationFn: () =>
      openapiAPI.searchAdvanced({ partita_iva: (lead.partita_iva || '').trim() }),
    onSuccess: async (res) => {
      queryClient.invalidateQueries({ queryKey: OPENAPI_QUOTA_QUERY_KEY });
      const item = res.data.results[0];
      if (!item) {
        toast.error('Nessun risultato per questa P.IVA');
        return;
      }
      await applyAnagraficaToLead(item);
    },
    onError: (e) => handleOpenapiError(e, 'advanced'),
  });

  const applyAnagraficaToLead = async (item: OpenapiSearchResultItem) => {
    const update: LeadUpdate = {
      tipo: 'azienda',
      ragione_sociale: item.denominazione,
      partita_iva: item.partita_iva,
    };
    if (item.codice_fiscale) update.codice_fiscale = item.codice_fiscale;
    if (item.pec) update.pec = item.pec;
    if (item.telefono) update.telefono = item.telefono;
    if (item.indirizzo?.via) update.indirizzo = item.indirizzo.via;
    if (item.indirizzo?.cap) update.cap = item.indirizzo.cap;
    if (item.indirizzo?.citta) update.citta = item.indirizzo.citta;
    if (item.indirizzo?.provincia) update.provincia = item.indirizzo.provincia;

    try {
      await leadsAPI.updateLead(lead.id, update);
      queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Anagrafica aggiornata');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento lead');
    }
  };

  // ----- Visura async -----
  const visuraStartMutation = useMutation({
    mutationFn: () =>
      openapiAPI.requestVisura({
        partita_iva: (lead.partita_iva || '').trim(),
        lead_id: lead.id,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: OPENAPI_QUOTA_QUERY_KEY });
      visuraAttempts.current = 0;
      setVisuraTimedOut(false);
      setVisuraExternalId(res.data.external_id);
      toast.success('Richiesta visura inviata');
    },
    onError: (e) => handleOpenapiError(e, 'visura'),
  });

  const visuraQuery = useQuery({
    queryKey: ['openapi-visura', visuraExternalId],
    queryFn: async () => {
      const res = await openapiAPI.pollVisura(visuraExternalId!);
      return res.data as OpenapiVisuraStatusResponse;
    },
    enabled: !!visuraExternalId && !visuraTimedOut,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'success' || status === 'error') return false;
      return VISURA_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!visuraExternalId) return;
    if (visuraQuery.isFetching) {
      visuraAttempts.current += 1;
      if (visuraAttempts.current > VISURA_POLL_MAX_ATTEMPTS) {
        setVisuraTimedOut(true);
      }
    }
  }, [visuraExternalId, visuraQuery.isFetching]);

  const visuraStatus = visuraQuery.data?.status;
  const visuraData = visuraQuery.data;

  const isAzienda = lead.tipo === 'azienda';
  const hasPiva = !!(lead.partita_iva && lead.partita_iva.trim());

  // ----- Render decision -----
  const cardClass =
    'bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden';

  const headerLabel = (
    <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
      <Building2 className="h-4 w-4 text-blue-600" />
      Dati Camera di Commercio
    </div>
  );

  // Stato: feature off -> blur + CTA
  if (!quotaQuery.isLoading && !featureEnabled) {
    return (
      <>
        <div className={cardClass}>
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            {headerLabel}
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
              <Lock className="h-3 w-3" />
              Bloccato
            </span>
          </div>
          <div className="relative p-4">
            <div
              className="space-y-2 text-sm"
              style={{ filter: 'blur(8px)', userSelect: 'none' }}
              aria-hidden="true"
            >
              <div className="font-semibold">{MOCK_DATA.denominazione}</div>
              <div className="text-gray-600">{MOCK_DATA.indirizzo}</div>
              <div className="text-gray-600">Capitale sociale: {MOCK_DATA.capitale}</div>
              <div className="text-gray-600">ATECO: {MOCK_DATA.ateco}</div>
              <div className="text-gray-600">PEC: {MOCK_DATA.pec}</div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/30 backdrop-blur-[2px] p-6 text-center">
              <ShieldCheck className="h-8 w-8 text-blue-600" />
              <p className="text-sm font-semibold text-gray-900">
                Sblocca dati ufficiali Camera di Commercio
              </p>
              <p className="text-xs text-gray-600 max-w-md">
                Validazione P.IVA in tempo reale, ricerca anagrafica completa e visure
                camerali ordinarie direttamente nel CRM.
              </p>
              <button
                type="button"
                onClick={() => setActivationOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Richiedi attivazione
              </button>
            </div>
          </div>
        </div>
        <RequestOpenapiActivationModal
          isOpen={activationOpen}
          onClose={() => setActivationOpen(false)}
        />
      </>
    );
  }

  // Stato: feature on
  return (
    <>
      <div className={cardClass}>
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          {headerLabel}
          {advancedRemaining !== null && (
            <span className="text-xs text-gray-500">
              Quota: {advancedRemaining}/{quotaQuery.data?.advanced.limit} · Visure{' '}
              {visuraRemaining}/{quotaQuery.data?.visura.limit}
            </span>
          )}
        </div>

        <div className="p-4 space-y-4">
          {!isAzienda ? (
            <p className="text-sm text-gray-500">
              I dati camerali sono disponibili solo per lead di tipo "Azienda".
            </p>
          ) : !hasPiva ? (
            <div className="text-sm text-gray-600 space-y-2">
              <p>Inserisci la P.IVA del lead per cercare i dati anagrafici ufficiali.</p>
              <p className="text-xs text-gray-500">
                Modifica il lead, compila il campo P.IVA, salva, poi torna qui.
              </p>
            </div>
          ) : (
            <>
              <CachedAnagraficaView lead={lead} />

              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  disabled={refreshMutation.isPending || advancedRemaining === 0}
                  onClick={() => {
                    if (advancedRemaining === 0) {
                      setUpgradeMode('quota_advanced');
                      return;
                    }
                    refreshMutation.mutate();
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {refreshMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Aggiorna anagrafica
                </button>
                <button
                  type="button"
                  disabled={
                    visuraStartMutation.isPending ||
                    !!visuraExternalId ||
                    visuraRemaining === 0
                  }
                  onClick={() => {
                    if (visuraRemaining === 0) {
                      setUpgradeMode('quota_visura');
                      return;
                    }
                    visuraStartMutation.mutate();
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {visuraStartMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <FileText className="h-3.5 w-3.5" />
                  )}
                  Richiedi visura
                </button>
                {advancedRemaining === 0 && (
                  <span className="text-[11px] px-2 py-0.5 bg-amber-200 text-amber-900 rounded self-center">
                    Quota anagrafica esaurita
                  </span>
                )}
              </div>

              <VisuraResultBlock
                externalId={visuraExternalId}
                status={visuraStatus}
                data={visuraData}
                timedOut={visuraTimedOut}
                attempts={visuraAttempts.current}
                onReset={() => {
                  setVisuraExternalId(null);
                  setVisuraTimedOut(false);
                  visuraAttempts.current = 0;
                }}
              />
            </>
          )}
        </div>
      </div>

      <RequestOpenapiUpgradeModal
        isOpen={upgradeMode !== null}
        mode={upgradeMode ?? 'feature'}
        onClose={() => setUpgradeMode(null)}
      />
    </>
  );
}

function CachedAnagraficaView({ lead }: { lead: LeadResponse }) {
  const indirizzoCompleto = useMemo(() => {
    const parts: string[] = [];
    if (lead.indirizzo) parts.push(lead.indirizzo);
    const cityLine = [lead.cap, lead.citta].filter(Boolean).join(' ');
    if (cityLine) parts.push(cityLine);
    if (lead.provincia) parts.push(`(${lead.provincia})`);
    return parts.join(' · ') || null;
  }, [lead.indirizzo, lead.cap, lead.citta, lead.provincia]);

  const row = (label: string, value?: string | null) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 text-right">{value || '—'}</span>
    </div>
  );

  return (
    <div className="text-sm">
      {row('Denominazione', lead.ragione_sociale)}
      {row('P.IVA', lead.partita_iva)}
      {row('Codice Fiscale', lead.codice_fiscale)}
      {row('Indirizzo legale', indirizzoCompleto)}
      {row('PEC', lead.pec)}
      {row('Telefono', lead.telefono)}
    </div>
  );
}

function VisuraResultBlock({
  externalId,
  status,
  data,
  timedOut,
  attempts,
  onReset,
}: {
  externalId: string | null;
  status?: 'pending' | 'success' | 'error';
  data?: OpenapiVisuraStatusResponse;
  timedOut: boolean;
  attempts: number;
  onReset: () => void;
}) {
  if (!externalId) return null;

  if (timedOut && status !== 'success' && status !== 'error') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 space-y-2">
        <p>
          La visura sta richiedendo più tempo del previsto. Controlla tra qualche minuto
          aggiornando la pagina o cliccando sotto.
        </p>
        <button
          type="button"
          onClick={onReset}
          className="text-xs underline font-medium"
        >
          Annulla operazione
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 space-y-2">
        <p>Errore durante l'elaborazione della visura.</p>
        <button
          type="button"
          onClick={onReset}
          className="text-xs underline font-medium"
        >
          Riprova
        </button>
      </div>
    );
  }

  if (status === 'success' && data) {
    const dati = data.dati_estratti;
    const soci = dati?.soci ?? [];
    const amm = dati?.amministratori ?? [];
    return (
      <div className="bg-green-50 border border-green-200 rounded p-3 text-sm space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-green-900">Visura ricevuta</span>
          {data.document_url && (
            <a
              href={data.document_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-700 underline"
            >
              <ExternalLink className="h-3 w-3" />
              Apri PDF
            </a>
          )}
        </div>
        {dati?.capitale_sociale != null && (
          <div className="text-sm text-gray-800">
            <span className="text-xs text-gray-500">Capitale sociale: </span>
            {String(dati.capitale_sociale)}
          </div>
        )}
        {amm.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Amministratori</div>
            <ul className="space-y-0.5">
              {amm.map((a, i) => (
                <li key={i} className="text-sm text-gray-800">
                  {a.nome ?? '—'}
                  {a.carica ? ` · ${a.carica}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        {soci.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Soci</div>
            <ul className="space-y-0.5">
              {soci.map((s, i) => (
                <li key={i} className="text-sm text-gray-800">
                  {s.nome ?? '—'}
                  {s.quota != null ? ` · ${s.quota}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          type="button"
          onClick={onReset}
          className="text-xs underline font-medium text-gray-600"
        >
          Chiudi
        </button>
      </div>
    );
  }

  // pending
  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 flex items-center gap-2">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>
        Visura in elaborazione… (tentativo {attempts}/{VISURA_POLL_MAX_ATTEMPTS})
      </span>
    </div>
  );
}
